import { Graphics } from 'pixi.js';
import { Boss, type BossDef } from './boss-base';
import { getTexture } from '@/assets/resolver';
import type { BulletSystem } from '@/entities/bullets';
import { bendFriendly } from '@/entities/bullet-fields';
import type { TelegraphSink } from '@/render/telegraphs';

/**
 * B9 — Eye of the Storm (air Elemental). Its signature is the
 * GUST: an invisible push current (the body winds up first) that SHOVES the
 * ship across the arena — you fight the wind while dodging crescent bullets.
 * It also calls TORNADO minions to crowd the field. docs/02 Elementals.
 * The EYE WALL (docs/02 B1) curls the player's shots that cross it — from
 * far away they spin off, from inside the calm eye they fly true; while a
 * gust blows the wall doubles. Its crescents curl outward (B3 curve).
 * Signatures: gust + tornado adds + eye wall (always), curling crescent
 * ring (P1/P2), crescent spiral (P3).
 */
export const GALE: BossDef = {
  id: 'gale',
  nameKey: 'boss.gale',
  tier: 4,
  minRoom: 2,
  baseHp: 1300, // the eye wall costs uptime — a lighter hull keeps TTK
  radius: 48,
  bodyKey: 'boss/gale/body',
  movement: 'strafe',
  contactDamage: 1,
  phases: [
    {
      hpPct: 1.0,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/wind', count: 9, interval: 2.2,
          spinDeg: 21, speed: 135, radius: 10, damage: 1, motion: { curveDeg: 28 },
        },
      ],
    },
    {
      hpPct: 0.62,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/wind', count: 11, interval: 2.0,
          spinDeg: 25, speed: 145, radius: 10, damage: 1, motion: { curveDeg: 28 },
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/wind', count: 5, spreadDeg: 40,
          interval: 2.4, telegraph: 0.4, speed: 230, radius: 10, damage: 1,
          unlockTier: 1,
        },
      ],
    },
    {
      hpPct: 0.3,
      speedMul: 1.35,
      patterns: [
        {
          type: 'spiral', bullet: 'bullet/enemy/wind', arms: 4, interval: 0.11,
          angularSpeed: 105, speed: 160, radius: 9, damage: 1,
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/wind', count: 5, spreadDeg: 46,
          interval: 2.2, telegraph: 0.35, speed: 250, radius: 10, damage: 1,
          unlockTier: 1,
        },
      ],
    },
  ],
  evolve: [
    {
      // Crosswind: a telegraphed double crescent pair
      tier: 2,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/wind', count: 2, spreadDeg: 12,
        interval: 3.4, telegraph: 0.45, burst: 2, burstGap: 0.28,
        speed: 255, radius: 10, damage: 1,
      },
    },
    {
      // Downburst: a slow heavy ring once the storm wall breaks
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'radial', bullet: 'bullet/enemy/ring', count: 10, interval: 4.2,
        spinDeg: 18, speed: 110, radius: 10, damage: 2,
      },
    },
  ],
};

const GUST_INTERVAL = 6.5;
const GUST_WINDUP = 1.0; // body swells before the current hits
const GUST_TIME = 1.6;
const GUST_PUSH = 240; // px/s flat current — fight it or get flung
const TORNADO_INTERVAL = 9;
const TORNADO_CAP = 4;
/** Eye wall: shots crossing the ring curl (rad/s); the calm eye is inside */
const EYE = { inner: 130, outer: 260, swirl: 1.0, gustMul: 2.8, streaks: 3, color: 0xd6f2ff };

export class GaleBoss extends Boss {
  private gustTimer = GUST_INTERVAL * 0.6;
  private gustWind = 0;
  private gust = 0;
  private gustDir = 0;
  private dirAngle = 0.9;
  private tornadoTimer = TORNADO_INTERVAL;
  private spin = 0;
  private readonly wall = new Graphics();

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(GALE, level, x, y, homeX, homeY);
    this.view.addChildAt(this.wall, 0);
  }

  private get blowing(): boolean {
    return this.gustWind > 0 || this.gust > 0;
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    super.update(dt, px, py, bullets, telegraphs);
    if (this.dead) return;

    // Gust cycle: windup → active push → rest
    if (this.gust > 0) {
      this.gust -= dt;
    } else if (this.gustWind > 0) {
      this.gustWind -= dt;
      if (this.gustWind <= 0) this.gust = GUST_TIME;
    } else {
      this.gustTimer -= dt;
      if (this.gustTimer <= 0) {
        this.gustTimer = GUST_INTERVAL / this.intensity;
        this.gustWind = GUST_WINDUP;
        // Pick a fresh sweep direction (golden-angle rotation)
        this.dirAngle += 2.39996;
        this.gustDir = this.dirAngle;
      }
    }

    // Eye wall curls the player's shots that cross it
    const swirl = EYE.swirl * (this.blowing ? EYE.gustMul : 1);
    bendFriendly(bullets, this.x, this.y, EYE.inner, EYE.outer, 0, swirl, dt);
    this.spin += swirl * dt;

    // Tornado adds: spin up weaver minions to crowd the arena
    this.tornadoTimer -= dt;
    if (this.tornadoTimer <= 0) {
      this.tornadoTimer = TORNADO_INTERVAL;
      if ((this.minionCount?.() ?? 0) < TORNADO_CAP) {
        this.spawnMinions?.('weaver', 2);
      }
    }
  }

  /** Push applied to the player while a gust blows (applied by combat/fight-step.ts). */
  override environmentalForce(px: number, py: number): { x: number; y: number } | null {
    return this.windPush(px, py);
  }

  windPush(px: number, py: number): { x: number; y: number } | null {
    void px; void py;
    if (this.gust <= 0 || this.dead) return null;
    return { x: Math.cos(this.gustDir) * GUST_PUSH, y: Math.sin(this.gustDir) * GUST_PUSH };
  }

  override render(alpha: number): void {
    super.render(alpha);
    const active = this.blowing;
    this.body.texture = getTexture('boss/gale/body', active ? 1 : 0);
    this.body.rotation += (active ? 0.06 : 0.03);
    // Eye wall: curved cloud streaks wheeling around the calm eye
    const g = this.wall;
    g.clear();
    const r = (EYE.inner + EYE.outer) / 2;
    for (let i = 0; i < EYE.streaks; i++) {
      const a = this.spin + (Math.PI * 2 * i) / EYE.streaks;
      g.arc(0, 0, r, a, a + 1.1).stroke({
        color: EYE.color, width: active ? 14 : 9, alpha: active ? 0.32 : 0.18, cap: 'round',
      });
    }
  }
}
